// Selection highlight for placed models (floor mode): recolour their black mesh
// edge lines to white while selected — for the single gizmo selection AND every
// object in a marquee group. Baked placements share the mesher's edge material,
// so we swap the LineSegments' material REFERENCE (not the shared material) on
// the selected placements only, and restore it when they're deselected.

import * as THREE from "three";

export class PlacementOutline {
  // White twin of the mesher's edge material (transparent + alpha 1 + LessEqual
  // depth) so the white lines sit on the face boundaries exactly like the black.
  private whiteMat: THREE.LineBasicMaterial;
  private swapped: {
    line: THREE.LineSegments;
    mat: THREE.Material | THREE.Material[];
  }[] = [];
  private current = new Set<THREE.Object3D>();

  constructor() {
    this.whiteMat = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 1.0,
    });
    this.whiteMat.depthFunc = THREE.LessEqualDepth;
  }

  // Highlight exactly this set of placement groups. No-op when the set is
  // unchanged, so it's cheap to call every frame.
  setTargets(groups: ReadonlyArray<THREE.Object3D>): void {
    if (
      groups.length === this.current.size &&
      groups.every((g) => this.current.has(g))
    ) {
      return;
    }
    this.restore();
    this.current = new Set(groups);
    for (const g of this.current) this.apply(g);
  }

  // Re-apply to the current set — call when a target's mesh was swapped (e.g. the
  // model was re-saved), which replaces the edge lines we recoloured.
  refresh(): void {
    const groups = [...this.current];
    this.restore();
    this.current.clear();
    this.setTargets(groups);
  }

  private apply(group: THREE.Object3D): void {
    group.traverse((o) => {
      const line = o as THREE.LineSegments;
      // The visible edge lines (the grid lines stay hidden on placements).
      if (line.isLineSegments && line.visible) {
        this.swapped.push({ line, mat: line.material });
        line.material = this.whiteMat;
      }
    });
  }

  private restore(): void {
    for (const s of this.swapped) s.line.material = s.mat;
    this.swapped.length = 0;
  }
}
